import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, InputNumber, Checkbox, message } from 'antd';
import api from '../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const AddBookingModal = ({ onClose, onSuccess }) => {
  const [hostels, setHostels] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const hostelId = Form.useWatch('hostel', form);
  const dateRange = Form.useWatch('dateRange', form);
  const roomId = Form.useWatch('room', form);

  useEffect(() => {
    api.get('/hostels').then((res) => setHostels(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!hostelId || !dateRange?.[0] || !dateRange?.[1]) {
      setAvailability(null);
      form.setFieldValue('room', undefined);
      form.setFieldValue('beds', undefined);
      return;
    }
    const checkIn = dateRange[0].format('YYYY-MM-DD');
    const checkOut = dateRange[1].format('YYYY-MM-DD');
    if (!dayjs(dateRange[1]).isAfter(dayjs(dateRange[0]))) {
      setAvailability(null);
      return;
    }
    setLoadingAvailability(true);
    api.get(`/hostels/${hostelId}/availability`, { params: { checkIn, checkOut } })
      .then((res) => setAvailability(res.data))
      .catch(() => setAvailability(null))
      .finally(() => setLoadingAvailability(false));
    form.setFieldValue('room', undefined);
    form.setFieldValue('beds', undefined);
  }, [hostelId, dateRange, form]);

  const availableRooms = availability?.rooms?.filter((r) => r.availableCount > 0) || [];
  const selectedRoomData = availability?.rooms?.find((r) => r._id === roomId);
  const availableBedsInRoom = selectedRoomData?.beds?.filter((b) => b.available) || [];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!values.room || !values.beds?.length) {
        message.error('Please select a room and at least one bed.');
        return;
      }
      setLoading(true);
      await api.post('/bookings', {
        hostel: values.hostel,
        guestName: values.guestName,
        guestEmail: values.guestEmail,
        guestPhone: values.guestPhone,
        checkIn: values.dateRange[0].format('YYYY-MM-DD'),
        checkOut: values.dateRange[1].format('YYYY-MM-DD'),
        room: values.room,
        beds: values.beds,
        notes: values.notes,
        totalAmount: values.totalAmount ?? 0,
      });
      message.success('Booking created');
      form.resetFields();
      onSuccess();
      onClose();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || err.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Add Booking"
      open
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      destroyOnClose
      okText="Create"
      width={560}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="hostel" label="Hostel" rules={[{ required: true }]}>
          <Select placeholder="Select hostel" options={hostels.map((h) => ({ value: h._id, label: h.name }))} />
        </Form.Item>
        <Form.Item name="dateRange" label="Check In / Check Out" rules={[{ required: true, message: 'Select dates' }]}>
          <RangePicker style={{ width: '100%' }} />
        </Form.Item>
        {hostelId && dateRange?.[0] && dateRange?.[1] && (
          <Form.Item name="room" label="Room" rules={[{ required: true }]}>
            <Select
              placeholder={loadingAvailability ? 'Loading...' : 'Select room'}
              loading={loadingAvailability}
              options={availableRooms.map((r) => ({
                value: r._id,
                label: `Room ${r.roomNumber} (${r.roomType}) – ${r.availableCount} bed(s)`,
              }))}
            />
          </Form.Item>
        )}
        {roomId && availableBedsInRoom.length > 0 && (
          <Form.Item name="beds" label="Beds (select at least one)" rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one bed' }]}>
            <Checkbox.Group options={availableBedsInRoom.map((b) => ({ value: b._id, label: `Bed ${b.bedNumber}` }))} />
          </Form.Item>
        )}
        <Form.Item name="guestName" label="Guest Name" rules={[{ required: true }]}>
          <Input placeholder="Guest name" />
        </Form.Item>
        <Form.Item name="guestEmail" label="Email">
          <Input type="email" placeholder="Email" />
        </Form.Item>
        <Form.Item name="guestPhone" label="Phone">
          <Input placeholder="Phone" />
        </Form.Item>
        <Form.Item name="totalAmount" label="Total Amount" initialValue={0}>
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="0" />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={3} placeholder="Notes" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddBookingModal;
